import React, { Profiler, ProfilerOnRenderCallback, ProfilerProps, ComponentType } from 'react';
import { isDebugEnabled } from '../config/environment';

/**
 * Type definition for React Profiler interactions
 */
export interface ProfilerInteraction {
  id: number;
  name: string;
  timestamp: number;
}

/**
 * Performance data logged by the profiler
 */
export interface PerformanceMetrics {
  id: string;
  phase: string;
  actualDuration: number;
  baseDuration: number;
  startTime: number;
  commitTime: number;
  interactions: Set<ProfilerInteraction>;
}

/**
 * Global flag to enable/disable performance monitoring
 * Uses the isDebugEnabled function to determine whether to enable monitoring
 */
export const ENABLE_PERFORMANCE_MONITORING = isDebugEnabled();

/**
 * Performance data buffer to store metrics
 */
const performanceMetrics: PerformanceMetrics[] = [];

/**
 * Callback function for the React Profiler
 */
export const onRenderCallback: ProfilerOnRenderCallback = (
  id: string,
  phase: 'mount' | 'update' | 'nested-update',
  actualDuration: number,
  baseDuration: number,
  startTime: number,
  commitTime: number,
  interactions?: Set<{ name: string; id: number; timestamp: number }>
) => {
  if (!ENABLE_PERFORMANCE_MONITORING) return;

  const metrics: PerformanceMetrics = {
    id,
    phase,
    actualDuration,
    baseDuration,
    startTime,
    commitTime,
    interactions: interactions as unknown as Set<ProfilerInteraction>
  };

  // Store the metrics
  performanceMetrics.push(metrics);

  // Log to console
  console.log(`[Performance] ${id} (${phase}): actual=${actualDuration.toFixed(2)}ms, base=${baseDuration.toFixed(2)}ms`);
};

/**
 * Component wrapper to profile performance
 * @param Component - The component to profile
 * @param id - The id for the profiler
 * @returns The wrapped component with profiling
 */
export function withProfiler<P extends JSX.IntrinsicAttributes>(
  Component: ComponentType<P>,
  id: string
): React.FC<P> {
  if (!ENABLE_PERFORMANCE_MONITORING) {
    return Component as React.FC<P>;
  }

  const ProfiledComponent: React.FC<P> = (props: P) => (
    <Profiler id={id} onRender={onRenderCallback}>
      <Component {...props} />
    </Profiler>
  );

  // Set display name for debugging
  ProfiledComponent.displayName = `Profiled(${Component.displayName || Component.name || 'Component'})`;

  return ProfiledComponent;
}

/**
 * Get all performance metrics that have been collected
 * @returns Array of performance metrics
 */
export function getPerformanceMetrics(): PerformanceMetrics[] {
  return [...performanceMetrics];
}

/**
 * Clear all performance metrics
 */
export function clearPerformanceMetrics(): void {
  performanceMetrics.length = 0;
}

/**
 * Log performance metrics to console for a specific component
 * @param componentId - The component ID to log metrics for
 */
export function logPerformanceMetrics(componentId: string): void {
  const metrics = performanceMetrics.filter(m => m.id === componentId);
  
  if (metrics.length === 0) {
    console.log(`No metrics found for component: ${componentId}`);
    return;
  }
  
  console.group(`Performance Metrics for ${componentId}`);
  
  const mountMetrics = metrics.filter(m => m.phase === 'mount');
  const updateMetrics = metrics.filter(m => m.phase === 'update');
  
  if (mountMetrics.length > 0) {
    const avgMountDuration = mountMetrics.reduce((sum, m) => sum + m.actualDuration, 0) / mountMetrics.length;
    console.log(`Mount: count=${mountMetrics.length}, avg=${avgMountDuration.toFixed(2)}ms`);
  }
  
  if (updateMetrics.length > 0) {
    const avgUpdateDuration = updateMetrics.reduce((sum, m) => sum + m.actualDuration, 0) / updateMetrics.length;
    console.log(`Update: count=${updateMetrics.length}, avg=${avgUpdateDuration.toFixed(2)}ms`);
  }
  
  console.groupEnd();
} 